import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Loader2, Eye, EyeOff, Upload, UserRound } from "lucide-react";

const MAX_PHOTO_DIM = 800; // px (longest side)
const PHOTO_QUALITY = 0.82;

async function resizeImage(file) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (e) => { img.src = e.target.result; };
        reader.onerror = reject;
        img.onload = () => {
            const ratio = Math.min(MAX_PHOTO_DIM / img.width, MAX_PHOTO_DIM / img.height, 1);
            const w = Math.round(img.width * ratio);
            const h = Math.round(img.height * ratio);
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", PHOTO_QUALITY));
        };
        img.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function PharmacistsTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { setItems((await api.get("/admin/pharmacists")).data || []); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (p) => {
        if (!window.confirm(`Supprimer ${p.name} ? Cela supprimera aussi ses disponibilités.`)) return;
        try { await api.delete(`/admin/pharmacists/${p.id}`); toast.success("Supprimé"); load(); }
        catch { toast.error("Échec"); }
    };

    return (
        <div data-testid="admin-pharmacists">
            <div className="flex justify-between items-end mb-6">
                <div>
                    <h2 className="font-display text-2xl text-brand-ink">Pharmaciens & équipe</h2>
                    <p className="font-sans text-sm text-brand-ink/60 mt-1">
                        {items.length} membre{items.length > 1 ? "s" : ""} — affichés sur la section <span className="italic">Équipe</span> du site
                    </p>
                </div>
                <button onClick={() => setCreating(true)} className="ll-btn ll-btn-primary" data-testid="add-pharmacist-btn">
                    <Plus className="h-4 w-4" /> Ajouter
                </button>
            </div>

            {loading ? <Loader2 className="h-6 w-6 animate-spin text-brand-primary" /> :
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(p => (
                        <div key={p.id} className="bg-white border border-brand-line p-5 flex items-start justify-between gap-3" data-testid={`pharmacist-card-${p.id}`}>
                            <div className="flex items-start gap-3 min-w-0">
                                {p.photo_data_url ? (
                                    <img src={p.photo_data_url} alt={p.name} className="h-14 w-14 rounded-full object-cover border-2" style={{borderColor: p.color}} />
                                ) : (
                                    <span className="h-14 w-14 rounded-full flex items-center justify-center font-display text-xl shrink-0" style={{background: p.color + "30", color: p.color}}>
                                        {p.name.charAt(0)}
                                    </span>
                                )}
                                <div className="min-w-0">
                                    <p className="font-display text-base leading-tight truncate">{p.name}</p>
                                    {p.role && <p className="font-sans text-xs text-brand-ink/70 truncate">{p.role}</p>}
                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                                        <span className={p.active ? "text-emerald-600" : "text-gray-400"}>
                                            {p.active ? "Actif" : "Inactif"}
                                        </span>
                                        <span className="text-brand-ink/40">•</span>
                                        <span className={`inline-flex items-center gap-1 ${p.show_on_team === false ? "text-gray-400" : "text-brand-primary"}`}>
                                            {p.show_on_team === false ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                            {p.show_on_team === false ? "Masqué" : "Sur le site"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button onClick={() => setEditing(p)} className="text-brand-primary" data-testid={`edit-pharmacist-${p.id}`}><Edit2 className="h-4 w-4" /></button>
                                <button onClick={() => remove(p)} className="text-red-600" data-testid={`delete-pharmacist-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            }

            {(editing || creating) && (
                <PharmacistDialog item={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }} />
            )}
        </div>
    );
}

function PharmacistDialog({ item, onClose, onSaved }) {
    const isEdit = !!item;
    const [form, setForm] = useState(item || {
        name: "",
        role: "",
        color: "#4e6d6f",
        active: true,
        show_on_team: true,
        photo_data_url: "",
        sort_order: 0,
    });
    const [saving, setSaving] = useState(false);
    const [processingPhoto, setProcessingPhoto] = useState(false);
    const fileRef = useRef(null);

    const handlePhoto = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Format image invalide"); return; }
        if (file.size > 8 * 1024 * 1024) { toast.error("Image trop volumineuse (max 8 Mo)"); return; }
        setProcessingPhoto(true);
        try {
            const dataUrl = await resizeImage(file);
            setForm((f) => ({ ...f, photo_data_url: dataUrl }));
        } catch {
            toast.error("Impossible de traiter l'image");
        } finally {
            setProcessingPhoto(false);
        }
    };

    const save = async () => {
        if (!form.name?.trim()) { toast.error("Nom requis"); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                role: (form.role || "").trim() || null,
                color: form.color,
                active: !!form.active,
                show_on_team: !!form.show_on_team,
                photo_data_url: form.photo_data_url || null,
                sort_order: Number.isFinite(+form.sort_order) ? +form.sort_order : 0,
            };
            if (isEdit) await api.put(`/admin/pharmacists/${item.id}`, payload);
            else await api.post(`/admin/pharmacists`, payload);
            toast.success("Enregistré");
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-brand-ink/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="pharmacist-dialog">
            <div className="bg-white max-w-lg w-full my-8">
                <div className="flex justify-between items-center p-5 border-b border-brand-line sticky top-0 bg-white">
                    <h3 className="font-display text-xl">{isEdit ? "Modifier le membre" : "Nouveau membre"}</h3>
                    <button onClick={onClose} data-testid="pharmacist-dialog-close"><X className="h-5 w-5" /></button>
                </div>
                <div className="p-6 space-y-5 font-sans">
                    {/* Photo */}
                    <div>
                        <label className="ll-eyebrow text-brand-ink/60 block mb-2">Photo</label>
                        <div className="flex items-center gap-4">
                            <div className="h-24 w-24 rounded-full overflow-hidden border-2 flex items-center justify-center shrink-0" style={{borderColor: form.color}}>
                                {form.photo_data_url ? (
                                    <img src={form.photo_data_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <UserRound className="h-10 w-10 text-brand-ink/30" />
                                )}
                            </div>
                            <div className="flex-1 flex flex-col gap-2">
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handlePhoto(e.target.files?.[0])}
                                    data-testid="pharmacist-photo-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    disabled={processingPhoto}
                                    className="ll-btn ll-btn-ghost text-sm disabled:opacity-60"
                                    data-testid="pharmacist-photo-upload-btn"
                                >
                                    {processingPhoto ? <><Loader2 className="h-4 w-4 animate-spin" /> Traitement…</> : <><Upload className="h-4 w-4" /> {form.photo_data_url ? "Changer la photo" : "Téléverser une photo"}</>}
                                </button>
                                {form.photo_data_url && (
                                    <button
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, photo_data_url: "" }))}
                                        className="text-xs text-red-600 self-start"
                                        data-testid="pharmacist-photo-remove-btn"
                                    >
                                        Retirer la photo
                                    </button>
                                )}
                                <p className="text-[11px] text-brand-ink/50">JPG ou PNG, redimensionnée à 800px max.</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="ll-eyebrow text-brand-ink/60 block mb-1">Nom complet</label>
                        <input
                            value={form.name}
                            onChange={e => setForm({...form, name: e.target.value})}
                            className="ll-input"
                            placeholder="Dr. Léa Morand"
                            data-testid="pharmacist-name-input"
                        />
                    </div>

                    <div>
                        <label className="ll-eyebrow text-brand-ink/60 block mb-1">Rôle / titre</label>
                        <input
                            value={form.role || ""}
                            onChange={e => setForm({...form, role: e.target.value})}
                            className="ll-input"
                            placeholder="Pharmacienne responsable"
                            data-testid="pharmacist-role-input"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="ll-eyebrow text-brand-ink/60 block mb-1">Couleur</label>
                            <input type="color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="h-10 w-20" />
                        </div>
                        <div>
                            <label className="ll-eyebrow text-brand-ink/60 block mb-1">Ordre d'affichage</label>
                            <input
                                type="number"
                                value={form.sort_order ?? 0}
                                onChange={e => setForm({...form, sort_order: e.target.value})}
                                className="ll-input"
                                data-testid="pharmacist-sort-input"
                            />
                        </div>
                    </div>

                    <div className="space-y-2 border-t border-brand-line pt-4">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={!!form.active} onChange={e => setForm({...form, active: e.target.checked})} data-testid="pharmacist-active-toggle" />
                            <span>Actif <span className="text-brand-ink/50">(disponible pour les RDV)</span></span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={!!form.show_on_team} onChange={e => setForm({...form, show_on_team: e.target.checked})} data-testid="pharmacist-show-team-toggle" />
                            <span>Afficher dans la section <span className="italic">Équipe</span> du site</span>
                        </label>
                    </div>
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-brand-line sticky bottom-0 bg-white">
                    <button onClick={onClose} className="ll-btn ll-btn-ghost">Annuler</button>
                    <button onClick={save} disabled={saving || processingPhoto} className="ll-btn ll-btn-primary disabled:opacity-60" data-testid="pharmacist-save-btn">
                        {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> …</> : "Enregistrer"}
                    </button>
                </div>
            </div>
        </div>
    );
}
